import { useEffect, useMemo, useRef, useState } from 'react';
import AppDateRangePickerModal from '../../../../general/AppDateRangePickerModal/AppDateRangePickerModal';
import { Buyer, DillTransaction, DocumentsSummary, formatMoney } from '@dill/dill-shared/dist/src';
import moment from 'moment';
import { ArrowBendDoubleUpRight, Bank, Coins, CreditCard, Calendar } from 'phosphor-react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getSupplierPosTransactions,
  getTransactionById,
} from '../../../../../redux/services/paymentsService';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import { DillButton } from '../../../../general/Dill/DillButton';
import AppTransactionStatusPill from '../../../../general/AppTransactionStatusPill/AppTransactionStatusPill';
import momentTz from 'moment-timezone';
import { SupplierAddWillCallPaymentModal } from '../../../payments/SupplierAddWillCallPaymentModal/SupplierAddWillCallPaymentModal';
import { AppPaidInvoicesModal } from '../../../../general/AppPaidInvoicesModal/AppPaidInvoicesModal';
import {
  getUserCustomers,
  recursiveGetUserCustomers,
} from '../../../../../redux/services/suppliersService';
import { DillTable, DillColumn } from '../../../../general/Dill/DillTable';
import { buildSupplierPath } from '../../../../../utils/helpers';

function VirtualPOSTransactionsTable() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, firebaseUserId } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  // Tracks the active supplier id for in-flight customer fetches, so a chain started for a
  // previous supplier stops once the supplier is switched (otherwise its results would be
  // written into the now differently-scoped store). The request ref lets us abort in-flight.
  const activeFetchSupplierIdRef = useRef<string | null>(currentSupplier?.id ?? null);
  const inFlightCustomersRequestRef = useRef<{ abort: () => void } | null>(null);
  useEffect(() => {
    activeFetchSupplierIdRef.current = currentSupplier?.id ?? null;
  }, [currentSupplier?.id]);
  useEffect(() => () => inFlightCustomersRequestRef.current?.abort(), [currentSupplier?.id]);
  const [isSupplierAddWillCallPaymentModal, setIsSupplierAddWillCallPaymentModal] = useState(false);
  const { supplierPosTransactions } = useAppSelector((state) => state.payments);
  const [isAppDateRangePickerModalOpen, setIsAppDateRangePickerModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState<{
    startDate?: Date;
    endDate?: Date;
  }>({
    startDate: moment(new Date()).subtract(3, 'months').toDate(),
    endDate: new Date(),
  });
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const validTimezone = momentTz.tz.zone(timezone) ? timezone : 'America/Los_Angeles';

  const [selectedTransaction, setSelectedTransaction] = useState<DillTransaction | null>(null);
  const [openPaidInvoiceDetails, setOpenPaidInvoiceDetails] = useState(false);
  const [previousLastDocumentId, setPreviousLastDocumentId] = useState('');
  const [loading, setLoading] = useState<boolean>(false);
  const [customerCount, setCustomerCount] = useState(0);
  const mainRoles = user?.roles.filter((role) => role !== 'DILL_ADMIN');

  const fetchPosTransactions = async () => {
    if (dateRange && user && currentSupplier?.id) {
      await dispatch(
        getSupplierPosTransactions({
          supplierId: currentSupplier?.id || '',
          startDate: dateRange?.startDate
            ? moment(dateRange?.startDate).format('MMM DD, YYYY')
            : '',
          endDate: dateRange?.endDate ? moment(dateRange?.endDate).format('MMM DD, YYYY') : '',
          timezone: validTimezone,
        })
      );
    }
  };

  useEffect(() => {
    if (dateRange && user && currentSupplier?.id) {
      fetchPosTransactions();
    }

    return () => {};
  }, [dateRange, user?.id, currentSupplier?.id]);

  const handleShowPaidInvoices = async (payment: DillTransaction) => {
    const transactionResults = await dispatch(getTransactionById({ transactionId: payment.id }));
    let finalTransaction = payment;
    if (transactionResults.type === 'payments/getTransactionById/fulfilled') {
      const res = transactionResults?.payload as any;
      finalTransaction = res.data as DillTransaction;
    }
    setSelectedTransaction(finalTransaction);
    setOpenPaidInvoiceDetails(true);
  };

  useEffect(() => {
    if (location.pathname.startsWith(buildSupplierPath(currentSupplier?.id, '/virtualPOS'))) {
      const params = new URLSearchParams(location.search);
      const transactionId = params.get('transactionId');
      if (transactionId) {
        const transaction = supplierPosTransactions.find((trans) => trans.id === transactionId);
        if (transaction) {
          handleShowPaidInvoices(transaction);
        }
      }
    }
  }, [location.pathname, supplierPosTransactions, location.search, currentSupplier?.id]);

  const recursiveFetchCustomers = async (
    count: number,
    lastDockId: string,
    firstCustomers: {
      buyer: Buyer;
      usersEmailsDetails: {
        email: string;
        userId: string;
        isInvitedUser: boolean;
        isStatementsEmailOptOut: boolean;
        isPaymentRemindersEmailOptOut: boolean;
      }[];
      documentsSummary: DocumentsSummary;
    }[],
    supplierId: string
  ) => {
    if (firstCustomers.length > count) {
      return;
    }
    setCustomerCount(count);
    const pageSize = 100;
    let currentPreviousLastDocumentId = lastDockId;
    for (let i = firstCustomers.length; i < count; i += pageSize) {
      // A newer fetch (e.g. after switching suppliers) supersedes this loop — stop so we
      // don't write the previous supplier's customers into the store.
      if (!supplierId || activeFetchSupplierIdRef.current !== supplierId) {
        break;
      }
      const request = dispatch(
        recursiveGetUserCustomers({
          supplierId,
          previousLastDocumentId: currentPreviousLastDocumentId,
          pageSize,
          sortBy: 'name',
          sortOrder: 'asc',
        })
      );
      inFlightCustomersRequestRef.current = request;
      const results = await request;
      if (activeFetchSupplierIdRef.current !== supplierId) {
        break;
      }
      if (results.type === 'supplier/recursiveGetUserCustomers/fulfilled') {
        if (results.payload) {
          const payload = results.payload as any;
          const fetchedCustomers: {
            buyer: Buyer;
            usersEmailsDetails: {
              email: string;
              isInvitedUser: boolean;
              isStatementsEmailOptOut: boolean;
              isPaymentRemindersEmailOptOut: boolean;
            }[];
            documentsSummary: DocumentsSummary;
          }[] = payload.data as any;
          const lastDocumentId = payload.lastDocumentId;
          if (lastDocumentId !== currentPreviousLastDocumentId) {
            setPreviousLastDocumentId(lastDocumentId);
            currentPreviousLastDocumentId = lastDocumentId;
          }
          if (!lastDockId || fetchedCustomers.length < pageSize) {
            console.log('No more pages to fetch');
            break;
          }
        }
      } else {
        console.log('Fetch failed or interrupted');
        break;
      }
    }
  };

  const handleLoadMore = async (loadOptions?: { isFreshFetch?: boolean }) => {
    const fetchSupplierId = currentSupplier?.id || '';
    if (!fetchSupplierId) {
      return;
    }
    setLoading(true);
    const pageSize = 100;
    let currentPreviousLastDocumentId = previousLastDocumentId;
    if (loadOptions && loadOptions?.isFreshFetch) {
      currentPreviousLastDocumentId = '';
    }
    const request = dispatch(
      getUserCustomers({
        supplierId: fetchSupplierId,
        previousLastDocumentId: currentPreviousLastDocumentId,
        pageSize,
        sortBy: 'name',
        sortOrder: 'asc',
      })
    );
    inFlightCustomersRequestRef.current = request;
    const results = await request;
    setLoading(false);
    if (activeFetchSupplierIdRef.current !== fetchSupplierId) {
      return;
    }
    if (results.type === 'supplier/getUserCustomers/fulfilled') {
      if (results.payload) {
        const payload = results.payload as any;
        const cust: {
          buyer: Buyer;
          usersEmailsDetails: {
            email: string;
            userId: string;
            isInvitedUser: boolean;
            isStatementsEmailOptOut: boolean;
            isPaymentRemindersEmailOptOut: boolean;
          }[];
          documentsSummary: DocumentsSummary;
        }[] = payload.data as any;
        const lastDockId = payload.lastDocumentId;
        if (!customerCount && payload.customerCount) {
          setCustomerCount(payload.customerCount || 0);
        }
        if (lastDockId && lastDockId !== currentPreviousLastDocumentId) {
          setPreviousLastDocumentId(lastDockId);

          const count = Number(payload.customerCount);
          recursiveFetchCustomers(count, lastDockId, [...cust], fetchSupplierId);
        }
      }
    }
  };

  useEffect(() => {
    if (mainRoles?.includes('SUPPLIER') && firebaseUserId) {
      setPreviousLastDocumentId('');
      handleLoadMore({ isFreshFetch: true });
    }

    return () => {};
  }, [user?.id, firebaseUserId, location.pathname]);

  const columns = useMemo((): DillColumn<DillTransaction>[] => {
    const getMethodType = (row: DillTransaction) =>
      row.type === 'DILL_FACILITATION_FEE_PAYMENT_BUYER' ||
      row.type === 'DILL_FACILITATION_FEE_PAYMENT_SUPPLIER'
        ? 'FEES'
        : row.type;

    return [
      {
        key: 'createdAt',
        label: 'Date',
        width: 100,
        sortable: true,
        getSortValue: (row) => (row.createdAt ? new Date(row.createdAt).getTime() : 0),
        render: (_, row) => (
          <p className="text-xs text-left w-full font-bold">
            {moment(row.createdAt).format('MM/DD/YYYY')}
          </p>
        ),
      },
      {
        key: 'amount',
        label: 'Amount',
        width: 100,
        render: (_, row) => {
          const methodType = getMethodType(row);
          const total = row.totalAmountPaid;
          let value = '';
          if (
            methodType === 'PAYMENT' ||
            methodType === 'WILL_CALL_PAYMENT' ||
            methodType === 'PAYMENT_REQUEST_PAYMENT' ||
            methodType === 'CREDIT_PAYMENT'
          ) {
            value = row?.transactionErrorMessage
              ? `+${formatMoney(total, 2)}`
              : `+${formatMoney(total - (row?.facilitationFee ?? 0), 2)}`;
          } else {
            value = `-${formatMoney(total, 2)}`;
          }
          return <p className="text-xs text-left w-full">{value}</p>;
        },
      },
      {
        key: 'transactionStatus',
        label: 'Transaction Status',
        width: 140,
        sortable: true,
        getSortValue: (row) => row.transactionStatus,
        render: (_, row) => (
          <div className="w-full mr-6">
            <AppTransactionStatusPill status={row.transactionStatus} />
          </div>
        ),
      },
      {
        key: 'method',
        label: 'Method',
        width: 150,
        sortable: false,
        render: (_, row) => {
          const methodType = getMethodType(row);
          if (methodType === 'FEES') return null;
          return (
            <div className="w-full flex">
              <div className="payment-type-logo">
                {row?.paymentMethod?.type === 'BANK_ACCOUNT' && row.type === 'WITHDRAW' ? (
                  <ArrowBendDoubleUpRight
                    size={24}
                    style={{ display: 'flex', alignSelf: 'center' }}
                  />
                ) : row?.paymentMethod?.type === 'BANK_ACCOUNT' ? (
                  <Bank size={24} style={{ display: 'flex', alignSelf: 'center' }} />
                ) : row.type === 'CREDIT_PAYMENT' ? (
                  <Coins size={24} style={{ display: 'flex', alignSelf: 'center' }} />
                ) : (
                  <CreditCard size={24} style={{ display: 'flex', alignSelf: 'center' }} />
                )}
              </div>
              <div>
                {row?.paymentMethod?.type === 'BANK_ACCOUNT' && row.type === 'WITHDRAW' ? (
                  <div className="text-xs font-bold">Payout</div>
                ) : row?.paymentMethod?.type === 'BANK_ACCOUNT' ? (
                  <div className="text-xs font-bold">Bank Transfer</div>
                ) : row.type === 'CREDIT_PAYMENT' ? (
                  <div className="text-xs font-bold">Credits</div>
                ) : row?.cardType ? (
                  <div className="text-xs font-bold">
                    {row?.cardType === 'CREDIT' ? 'Credit Card' : 'Debit / Other Card'}
                  </div>
                ) : row?.paymentMethod?.cardType ? (
                  <div className="text-xs font-bold">
                    {row?.paymentMethod?.cardType === 'CREDIT'
                      ? 'Credit Card'
                      : 'Debit / Other Card'}
                  </div>
                ) : (
                  <div className="text-xs font-bold">Credit/Debit Card</div>
                )}
                <div className="payment-method-details">{`${
                  row.type === 'PAYMENT_REQUEST_PAYMENT' || row.type === 'WILL_CALL_PAYMENT'
                    ? row?.paymentMethodName ?? ''
                    : row?.paymentMethod?.paymentMethodName ?? ''
                }`}</div>
              </div>
            </div>
          );
        },
      },
      {
        key: 'viewDetails',
        label: '',
        width: 50,
        sortable: false,
        render: (_, row) => {
          const methodType = getMethodType(row);
          if (methodType === 'FEES') return null;
          return (
            <DillButton
              label="Details"
              variant="secondary"
              onClick={() => {
                handleShowPaidInvoices(row);
                if (
                  location.pathname.startsWith(
                    buildSupplierPath(currentSupplier?.id, '/virtualPOS')
                  )
                ) {
                  navigate(
                    buildSupplierPath(currentSupplier?.id, `/virtualPOS?transactionId=${row.id}`)
                  );
                }
              }}
            />
          );
        },
      },
    ];
  }, [location.pathname, navigate]);

  return (
    <div className="h-full w-full">
      <AppPaidInvoicesModal
        open={openPaidInvoiceDetails}
        handleClose={() => {
          setOpenPaidInvoiceDetails(false);
          if (location.pathname.startsWith(buildSupplierPath(currentSupplier?.id, '/virtualPOS'))) {
            navigate(buildSupplierPath(currentSupplier?.id, `/virtualPOS`));
          }
        }}
        accountType="supplier"
        selectedTransaction={selectedTransaction}
        onViewMainPayment={() => {}}
      />
      <SupplierAddWillCallPaymentModal
        title="Add POS Payment"
        open={isSupplierAddWillCallPaymentModal}
        handleClose={() => {
          setIsSupplierAddWillCallPaymentModal(false);
        }}
        handlePaymentDone={() => {
          setIsSupplierAddWillCallPaymentModal(false);
          fetchPosTransactions();
        }}
      />
      <AppDateRangePickerModal
        open={isAppDateRangePickerModalOpen}
        selectedDateRange={dateRange}
        handleClose={() => {
          setIsAppDateRangePickerModalOpen(false);
        }}
        onSelectDateRange={(selection) => {
          setIsAppDateRangePickerModalOpen(false);
          setDateRange(selection);
        }}
      />

      <DillTable
        columns={columns}
        data={supplierPosTransactions}
        rowKey={(row) => row.id}
        sortable
        virtualize
        rowHeight={70}
        resizable
        striped={false}
        emptyMessage="No Payments"
        showCount={false}
        toolbarLeft={
          <div className="flex items-center">
            <button
              onClick={() => setIsAppDateRangePickerModalOpen(true)}
              className="flex flex-row items-center GREY_200-BORDER border rounded-[6px] py-[4px] px-2 GREY_100-BG hover:bg-slate-200 WHITE-BG mt-[1px]">
              <Calendar className="text-slate-500 mr-2" size={20} />
              <div className="text-sm TEXT_PRIMARY-CLR">{`${
                dateRange?.startDate ? moment(dateRange?.startDate).format('MMM DD, YYYY') : ''
              }-${
                dateRange?.endDate ? moment(dateRange?.endDate).format('MMM DD, YYYY') : ''
              }`}</div>
            </button>
          </div>
        }
        addButton={{
          label: 'Add POS Payment',
          onClick: () => setIsSupplierAddWillCallPaymentModal(true),
        }}
      />
    </div>
  );
}

export default VirtualPOSTransactionsTable;
